import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Dropdown, DropdownMenu, DropdownToggle } from 'reactstrap';
import { ThemedDropdownItem } from '../../components/themed/ThemedComponents';
import Setting from './Setting';
import SettingSelect from './SettingSelect';
import CompanyEditContainer from '../companies/CompanyEditor';
import Modal from 'react-modal';
import { useStores } from '../../hooks/use-stores';
import {
    Block,
    FlexDiv,
    Image,
    SpaceBetweenDiv,
    TextDefault,
    TextInput,
    TextLead,
    Heading2,
    Heading3,
    ThemedButton,
    CheckboxInput,
} from '@awarego-ui/components';
import SettingMultiSelect from './SettingMultiSelect';
import CompanyGenericEditor from '../companies/CompanyGenericEditor';
import AutomationReminders from '../../components/manager/automation/reminders';
import {
    AUTOMATION_SCHEDULE_TYPE,
    REMINDER_DELAY_TYPE,
} from '../../utils/constants';
import {
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    Checkbox,
    TextField,
    CircularProgress,
} from '@mui/material';
import { LocalizationProvider, DateTimePicker, PickersActionBar } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import services from '../../services';
import { TextTiny } from '@awarego-ui/components';
import ErrorMessage from '../../components/errormessage';
import AwareExposeSettingsModal from './AwareExposeSettingsModal';

const SECURITY_DURATION_OPTIONS = [
    { key: '1h', value: '1h', name: '1 hour' },
    { key: '12h', value: '12h', name: '12 hours' },
    { key: '1d', value: '1d', name: '1 day' },
    { key: '1w', value: '1w', name: '1 week' },
    { key: '30d', value: '30d', name: '1 month' },
];

const brandingFields = {
    'ui.minihra_banner_image_src': {
        label: 'Banner image URL',
        rules: 'httpsUrl',
    },
    'ui.minihra_banner_link_url': {
        label: 'Banner link URL',
        rules: 'httpsUrl',
    },
    'ui.minihra_finish_url': {
        label: 'Finish URL (Learn more about HRA button)',
        rules: 'httpsUrl',
    },
};

function compareOnboardingPreviewUsers(a, b, field) {
    if (field === 'dateAdded') {
        const ta = a.dateAdded ? new Date(a.dateAdded).getTime() : 0;
        const tb = b.dateAdded ? new Date(b.dateAdded).getTime() : 0;
        return ta - tb;
    }
    if (field === 'lists') {
        const sa = (a.lists || []).join(', ');
        const sb = (b.lists || []).join(', ');
        return sa.localeCompare(sb, undefined, { sensitivity: 'base' });
    }
    const sa = String(
        field === 'name' ? a.name || a.email || '' : a[field] || ''
    );
    const sb = String(
        field === 'name' ? b.name || b.email || '' : b[field] || ''
    );
    return sa.localeCompare(sb, undefined, { sensitivity: 'base' });
}

const ONBOARDING_PREVIEW_SORT_COLUMNS = [
    { field: 'email', label: 'Email' },
    { field: 'name', label: 'Name' },
    { field: 'lists', label: 'Lists' },
    { field: 'dateAdded', label: 'Date added' },
];

const pricacyFields = {
    'ui.privacy_url': {
        label: 'Privacy Policy URL',
        rules: 'httpsUrl',
    }
}

export default observer(() => {
    const { store, commonStore, companyStore, authStore } = useStores();
    const { token, currentUser } = authStore;
    const { serverData, currentCompany, currentCompanyUISettings, languages, enabledLanguages } =
        store;

    let navigate = useNavigate();

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [showEditCompanyModal, setShowEditCompanyModal] = useState(false);

    const [showBrandingModal, setShowBrandingModal] = useState(false);

    const [showPrivacyModal, setShowPrivacyModal] = useState(false);
    const [onboardingNameTemplateDraft, setOnboardingNameTemplateDraft] =
        useState(null);
    const [onboardingRemindersEnabled, setOnboardingRemindersEnabled] = useState(false);
    const [onboardingRemindersChannel, setOnboardingRemindersChannel] = useState(1);
    const [onboardingRemindersSteps, setOnboardingRemindersSteps] = useState([]);
    const [onboardingConfigModalOpen, setOnboardingConfigModalOpen] = useState(false);
    const [onboardingModalDraft, setOnboardingModalDraft] = useState(null);
    const [onboardingPreviewModalOpen, setOnboardingPreviewModalOpen] = useState(false);
    const [onboardingPreviewData, setOnboardingPreviewData] = useState(null);
    const [onboardingPreviewLoading, setOnboardingPreviewLoading] = useState(false);
    const [onboardingPreviewError, setOnboardingPreviewError] = useState(null);
    const [onboardingPreviewSelectedEmails, setOnboardingPreviewSelectedEmails] = useState(new Set());
    const [onboardingPreviewSort, setOnboardingPreviewSort] = useState({
        field: 'dateAdded',
        dir: 'desc',
    });
    const [onboardingAddUserInput, setOnboardingAddUserInput] = useState('');
    const [onboardingReschedulePickerOpen, setOnboardingReschedulePickerOpen] = useState(false);
    const [onboardingRescheduleDraft, setOnboardingRescheduleDraft] = useState(null);
    const [onboardingRescheduleLoading, setOnboardingRescheduleLoading] = useState(false);
    const [onboardingPeriodFromPickerOpen, setOnboardingPeriodFromPickerOpen] = useState(false);
    const [onboardingPeriodFromDraft, setOnboardingPeriodFromDraft] = useState(null);
    const [onboardingPeriodFromLoading, setOnboardingPeriodFromLoading] = useState(false);
    const [onboardingContextLoading, setOnboardingContextLoading] = useState(false);
    const [onboardingRemovingExcludedEmail, setOnboardingRemovingExcludedEmail] = useState(null);
    const [awareExposeModalOpen, setAwareExposeModalOpen] = useState(false);
    const [tagColumnDraft, setTagColumnDraft] = useState(
        currentCompanyUISettings['ui.Tag_column'] || ''
    );
    const [tagColumnSavedMark, setTagColumnSavedMark] = useState(false);

    const OnboardingRescheduleActionBar = (props) => {
        const { onCancel, ...rest } = props;
        return (
            <PickersActionBar
                {...rest}
                onCancel={() => {
                    setOnboardingRescheduleDraft(null);
                    onCancel();
                }}
            />
        );
    };

    useEffect(() => {
        setTagColumnDraft(currentCompanyUISettings['ui.Tag_column'] || '');
        setTagColumnSavedMark(false);
    }, [currentCompany?.company_id, currentCompanyUISettings['ui.Tag_column']]);

    useEffect(() => {
        if (currentCompany?.company_id) {
            companyStore.loadOnboardingSettings(currentCompany.company_id);
        }
    }, [currentCompany?.company_id]);

    useEffect(() => {
        setOnboardingNameTemplateDraft(null);
    }, [currentCompany?.company_id, companyStore.onboardingSettings?.nameTemplate]);

    useEffect(() => {
        const s = companyStore.onboardingSettings;
        if (s) {
            setOnboardingRemindersEnabled(!!s.remindersEnabled);
            setOnboardingRemindersChannel(
                typeof s.remindersSettings?.channel === 'number' ? s.remindersSettings.channel : 1
            );
            setOnboardingRemindersSteps(
                Array.isArray(s.remindersSettings?.steps)
                    ? s.remindersSettings.steps.map((x) => ({ ...x }))
                    : []
            );
        }
    }, [currentCompany?.company_id, companyStore.onboardingSettings]);

    const setEnabledLanguages = async (value) => {
        store.currentCompany?.languages.forEach((lang) => {
            lang.enabled = value.includes(lang.code) || lang.code === (currentCompany.defaultLanguage ?? 'en');
        });

        return new Promise((resolve) => {
            companyStore.saveLanguageSettings(store.currentCompany.company_id, store.currentCompany.languages, resolve);
        });
    };

    const toggleCompanyDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };
    const openEditModal = () => {
        setShowEditCompanyModal(true);
    };

    const closeEditModal = () => {
        setShowEditCompanyModal(false);
    };

    const onCustomizeAppearance = () => {
        navigate('appearance');
    };

    const onChangeLanguage = async (languageKey) => {
        store.currentCompany.defaultLanguage = languageKey;
        await store.saveCurrentCompany({
            ...{ defaultLanguage: languageKey },
        });
    };


    const downloadCertificate = () => {
        store.downloadPreviewCertificate(
            store.currentCompany.company_id,
            currentUser.name,
            token
        );
    };

    const onMakeAnonymous = () => {
        commonStore.showConfirm(
            "Please note that once user logging has been disabled, you can't enable it again later. All previously collected data will be made anonymous.",
            'Disable user logging',
            'Are you sure you want to proceed?',
            async () => {
                await companyStore.switchTrackingAnonymous(
                    store.currentCompany.company_id
                );
                await store.saveCurrentCompany({});
            },
            true
        );
    };

    const onChangeNewEmployeeEnrolment = async () => {
        let value =
            store.currentCompanyUISettings['ui.sendEnrolment'] === '1'
                ? '0'
                : '1';
        await companyStore.saveEnrolmentSetting(
            store.currentCompany.company_id,
            value
        );
    };

    const onChangeInviteOnListAdd = async () => {
        const value =
            store.currentCompanyUISettings['ui.assessment.inviteOnListAdd'] ===
            '1'
                ? '0'
                : '1';
        await companyStore.saveInviteOnListAddSetting(
            store.currentCompany.company_id,
            value
        );
    };

    const onboardingSettings = companyStore.onboardingSettings;
    const onboardingEnabled = onboardingSettings?.enabled ?? false;
    const onboardingTemplates =
        onboardingSettings?.templates?.map((t) => ({
            key: t.id,
            value: String(t.id),
            name: t.title,
        })) ?? [];

    const fastForwardDisabled =
        currentCompanyUISettings['ui.disable_fast_forward'] === '1';

    const organizeListsByOrganizationEnabled =
        currentCompanyUISettings[
            'ui.organize_employee_lists_by_organization'
        ] === '1';

    const hideObsoleteBouncesEnabled =
        currentCompanyUISettings['ui.bounced.hideObsolete'] === '1';

    const onToggleOrganizeListsByOrganization = async () => {
        const companyId = store.currentCompany.company_id;
        const nextValue = organizeListsByOrganizationEnabled ? '0' : '1';
        try {
            await companyStore.saveOrganizeEmployeeListsByOrganizationSetting(
                companyId,
                nextValue
            );
        } catch (e) {
            /* error surfaced via companyStore.error */
        }
    };

    const onSaveTagColumn = async () => {
        const companyId = store.currentCompany.company_id;
        const next = String(tagColumnDraft || '').trim();
        const current = String(
            currentCompanyUISettings['ui.Tag_column'] || ''
        ).trim();
        if (next === current) return;
        try {
            await companyStore.saveTagColumnSetting(companyId, next);
            setTagColumnDraft(next);
            setTagColumnSavedMark(true);
        } catch (e) {
            setTagColumnDraft(current);
            commonStore.error(companyStore.error || 'Failed to save');
        }
    };

    const onToggleHideObsoleteBounces = async () => {
        const companyId = store.currentCompany.company_id;
        const nextValue = hideObsoleteBouncesEnabled ? '0' : '1';
        try {
            await companyStore.saveHideObsoleteBouncesSetting(
                companyId,
                nextValue
            );
        } catch (e) {
            commonStore.error(companyStore.error || 'Failed to save');
        }
    };

    const onToggleFastForward = async () => {
        const companyId = store.currentCompany.company_id;
        const nextValue = fastForwardDisabled ? '0' : '1';
        try {
            await companyStore.saveFastForwardSetting(companyId, nextValue);
        } catch (e) {
            commonStore.error(companyStore.error || 'Failed to save');
        }
    };

    const onChangeOnboardingToggle = async () => {
        const companyId = store.currentCompany.company_id;
        const newEnabled = !onboardingEnabled;
        if (newEnabled && onboardingTemplates.length === 0) {
            commonStore.error('No training templates available');
            return;
        }
        const doSave = async () => {
            try {
                await companyStore.saveOnboardingSettings(companyId, {
                    enabled: newEnabled,
                    templateId: newEnabled
                        ? (onboardingSettings?.templateId ??
                          onboardingTemplates[0]?.value ??
                          3)
                        : null,
                    ...(newEnabled
                        ? {
                              createAsDraft:
                                  onboardingSettings?.createAsDraft !== false,
                          }
                        : {}),
                });
            } catch (e) {
                commonStore.error(companyStore.error || 'Failed to save');
            }
        };
        if (newEnabled) {
            await doSave();
        } else {
            commonStore.showConfirm(
                'Automated monthly onboarding training will be disabled. New employees will no longer be automatically assigned onboarding campaigns.',
                'Disable',
                'Disable onboarding automation?',
                doSave,
                false
            );
        }
    };

    const onChangeOnboardingTemplate = async (templateId) => {
        const companyId = store.currentCompany.company_id;
        try {
            await companyStore.saveOnboardingSettings(companyId, {
                enabled: true,
                templateId: Number(templateId),
            });
        } catch (e) {
            commonStore.error(companyStore.error || 'Failed to save');
        }
    };

    const onChangeOnboardingNameTemplate = async (nameTemplate) => {
        const companyId = store.currentCompany.company_id;
        const templateId =
            onboardingSettings?.templateId ?? onboardingTemplates[0]?.value;
        try {
            await companyStore.saveOnboardingSettings(companyId, {
                enabled: true,
                templateId: templateId ? Number(templateId) : undefined,
                nameTemplate: nameTemplate?.trim() || 'Employee Onboarding {{MMMM}} {{YYYY}}',
            });
        } catch (e) {
            commonStore.error(companyStore.error || 'Failed to save');
        }
    };

    const onChangeOnboardingSendingTime = async (sendingTime) => {
        const companyId = store.currentCompany.company_id;
        const templateId =
            onboardingSettings?.templateId ?? onboardingTemplates[0]?.value;
        try {
            await companyStore.saveOnboardingSettings(companyId, {
                enabled: true,
                templateId: templateId ? Number(templateId) : undefined,
                sendingTime: sendingTime || '08:00',
            });
        } catch (e) {
            commonStore.error(companyStore.error || 'Failed to save');
        }
    };

    const saveOnboardingReminders = async (payload) => {
        const companyId = store.currentCompany.company_id;
        const templateId =
            onboardingSettings?.templateId ?? onboardingTemplates[0]?.value;
        try {
            await companyStore.saveOnboardingSettings(companyId, {
                enabled: true,
                templateId: templateId ? Number(templateId) : undefined,
                ...payload,
            });
        } catch (e) {
            commonStore.error(companyStore.error || 'Failed to save');
        }
    };

    const onChangeOnboardingRemindersEnabled = (e) => {
        const enabled = typeof e === 'boolean' ? e : e?.target?.checked ?? !onboardingRemindersEnabled;
        setOnboardingRemindersEnabled(enabled);
        saveOnboardingReminders({
            remindersEnabled: enabled,
            remindersSettings: {
                channel: onboardingRemindersChannel,
                steps: enabled ? onboardingRemindersSteps : [],
            },
        });
    };

    const onChangeOnboardingRemindersChannel = (channel) => {
        setOnboardingRemindersChannel(channel);
        saveOnboardingReminders({
            remindersEnabled: onboardingRemindersEnabled,
            remindersSettings: {
                channel,
                steps: onboardingRemindersSteps,
            },
        });
    };

    const onAddOnboardingReminderStep = () => {
        const steps = [
            ...onboardingRemindersSteps,
            { delay: 1, delayType: REMINDER_DELAY_TYPE.DAY },
        ];
        setOnboardingRemindersSteps(steps);
        saveOnboardingReminders({
            remindersEnabled: true,
            remindersSettings: { channel: onboardingRemindersChannel, steps },
        });
    };

    const onDeleteOnboardingReminderStep = (step) => {
        const steps = onboardingRemindersSteps.filter((s) => s !== step);
        setOnboardingRemindersSteps(steps);
        saveOnboardingReminders({
            remindersEnabled: onboardingRemindersEnabled,
            remindersSettings: { channel: onboardingRemindersChannel, steps },
        });
    };

    const onOnboardingReminderStepsChange = () => {
        saveOnboardingReminders({
            remindersEnabled: onboardingRemindersEnabled,
            remindersSettings: {
                channel: onboardingRemindersChannel,
                steps: [...onboardingRemindersSteps],
            },
        });
    };

    const openOnboardingConfigModal = () => {
        const s = companyStore.onboardingSettings;
        if (!s) return;
        setOnboardingModalDraft({
            templateId: String(s.templateId ?? onboardingTemplates[0]?.value ?? ''),
            nameTemplate: s.nameTemplate ?? 'Employee Onboarding {{MMMM}} {{YYYY}}',
            sendingTime: s.sendingTime ?? '08:00',
            remindersEnabled: !!s.remindersEnabled,
            remindersChannel: typeof s.remindersSettings?.channel === 'number' ? s.remindersSettings.channel : 1,
            remindersSteps: Array.isArray(s.remindersSettings?.steps)
                ? s.remindersSettings.steps.map((x) => ({ ...x }))
                : [],
            lastSuccessfulRun: s.lastSuccessfulRun || s.periodFrom || moment().startOf('day').toISOString(),
            createAsDraft: s.createAsDraft !== false,
        });
        setOnboardingConfigModalOpen(true);
    };

    const closeOnboardingConfigModal = () => {
        setOnboardingConfigModalOpen(false);
        setOnboardingModalDraft(null);
    };

    const openOnboardingPreviewModal = async () => {
        setOnboardingPreviewModalOpen(true);
        setOnboardingPreviewError(null);
        setOnboardingPreviewSelectedEmails(new Set());
        setOnboardingAddUserInput('');
        setOnboardingRescheduleDraft(null);
        setOnboardingReschedulePickerOpen(false);
        setOnboardingPeriodFromDraft(null);
        setOnboardingPeriodFromPickerOpen(false);
        const companyId = store.currentCompany?.company_id;
        if (!companyId) return;
        setOnboardingPreviewLoading(true);
        try {
            const data = await services.Companies.getOnboardingPreview(companyId);
            setOnboardingPreviewData(data);
        } catch (e) {
            setOnboardingPreviewError(e?.message || 'Failed to load preview');
            setOnboardingPreviewData(null);
        } finally {
            setOnboardingPreviewLoading(false);
        }
    };

    const closeOnboardingPreviewModal = () => {
        setOnboardingPreviewModalOpen(false);
        setOnboardingPreviewData(null);
        setOnboardingPreviewSelectedEmails(new Set());
        setOnboardingRescheduleDraft(null);
        setOnboardingReschedulePickerOpen(false);
        setOnboardingPeriodFromDraft(null);
        setOnboardingPeriodFromPickerOpen(false);
    };

    const refetchOnboardingPreview = async () => {
        const companyId = store.currentCompany?.company_id;
        if (!companyId || !onboardingPreviewModalOpen) return;
        try {
            const data = await services.Companies.getOnboardingPreview(companyId);
            setOnboardingPreviewData(data);
        } catch (_) {
            // keep previous data
        }
    };

    const handleOnboardingReschedule = async (date) => {
        const companyId = store.currentCompany?.company_id;
        if (!companyId || !date) return;
        const m = moment(date);
        if (!m.isValid() || m.isSameOrBefore(moment())) {
            commonStore.error('Please select a future date and time');
            return;
        }
        setOnboardingRescheduleLoading(true);
        try {
            const scheduledAt = m.toISOString();
            const data = await services.Companies.rescheduleOnboarding(companyId, {
                scheduledAt,
            });
            setOnboardingPreviewData(data);
            setOnboardingReschedulePickerOpen(false);
            setOnboardingRescheduleDraft(null);
            commonStore.success('Onboarding run rescheduled');
        } catch (e) {
            commonStore.error(e?.message || 'Failed to reschedule');
        } finally {
            setOnboardingRescheduleLoading(false);
        }
    };

    const handleOnboardingPeriodFrom = async (date) => {
        const companyId = store.currentCompany?.company_id;
        if (!companyId || !date) return;
        const m = moment(date);
        if (!m.isValid()) {
            commonStore.error('Please select a valid date and time');
            return;
        }
        setOnboardingPeriodFromLoading(true);
        try {
            const data = await services.Companies.updateOnboardingPeriodFrom(
                companyId,
                { lastSuccessfulRun: m.toISOString() }
            );
            setOnboardingPreviewData(data);
            setOnboardingPeriodFromPickerOpen(false);
            setOnboardingPeriodFromDraft(null);
            await companyStore.loadOnboardingSettings(companyId);
            commonStore.success('Next period from updated');
        } catch (e) {
            commonStore.error(e?.message || 'Failed to update period from');
        } finally {
            setOnboardingPeriodFromLoading(false);
        }
    };

    const handleOnboardingAddUser = async () => {
        const email = (onboardingAddUserInput || '').trim();
        if (!email) return;
        const companyId = store.currentCompany?.company_id;
        if (!companyId) return;
        setOnboardingContextLoading(true);
        try {
            const currentIncluded = onboardingPreviewData?.includedEmails ?? [];
            await services.Companies.updateOnboardingContext(companyId, {
                includedEmails: [...currentIncluded, email],
            });
            setOnboardingAddUserInput('');
            await refetchOnboardingPreview();
            commonStore.success('User added to next onboarding');
        } catch (e) {
            commonStore.error(e?.message || 'Failed to add user');
        } finally {
            setOnboardingContextLoading(false);
        }
    };

    const handleOnboardingExcludeSelected = async () => {
        if (onboardingPreviewSelectedEmails.size === 0) return;
        const companyId = store.currentCompany?.company_id;
        if (!companyId) return;
        setOnboardingContextLoading(true);
        try {
            const currentExcluded = onboardingPreviewData?.excludedEmails ?? [];
            const currentIncluded = onboardingPreviewData?.includedEmails ?? [];
            const selectedList = Array.from(onboardingPreviewSelectedEmails);
            const currentIncludedSet = new Set(
                currentIncluded.map((e) => (e || '').toLowerCase())
            );
            const newIncluded = currentIncluded.filter(
                (e) => !onboardingPreviewSelectedEmails.has(e)
            );
            const addToExcluded = selectedList.filter(
                (e) => !currentIncludedSet.has((e || '').toLowerCase())
            );
            const newExcluded = [
                ...currentExcluded,
                ...addToExcluded,
            ];
            await services.Companies.updateOnboardingContext(companyId, {
                excludedEmails: newExcluded,
                includedEmails: newIncluded,
            });
            setOnboardingPreviewSelectedEmails(new Set());
            await refetchOnboardingPreview();
            commonStore.success('Selected users excluded from next onboarding');
        } catch (e) {
            commonStore.error(e?.message || 'Failed to exclude users');
        } finally {
            setOnboardingContextLoading(false);
        }
    };

    const handleOnboardingRemoveExcluded = (email) => {
        commonStore.showConfirm(
            'Remove from excluded list? They will be included in the next onboarding run.',
            'Remove',
            'Remove from excluded',
            async () => {
                const companyId = store.currentCompany?.company_id;
                if (!companyId) return;
                setOnboardingRemovingExcludedEmail(email);
                try {
                    const currentExcluded = onboardingPreviewData?.excludedEmails ?? [];
                    await services.Companies.updateOnboardingContext(companyId, {
                        excludedEmails: currentExcluded.filter((e) => e !== email),
                    });
                    await refetchOnboardingPreview();
                    commonStore.success('User removed from excluded list');
                } catch (e) {
                    commonStore.error(e?.message || 'Failed to remove');
                } finally {
                    setOnboardingRemovingExcludedEmail(null);
                }
            },
            false,
            true
        );
    };

    const toggleOnboardingPreviewUserSelection = (email) => {
        setOnboardingPreviewSelectedEmails((prev) => {
            const next = new Set(prev);
            if (next.has(email)) next.delete(email);
            else next.add(email);
            return next;
        });
    };

    const toggleOnboardingPreviewSelectAll = () => {
        const users = onboardingPreviewData?.users ?? [];
        const allSelected =
            users.length > 0 &&
            users.every((u) => onboardingPreviewSelectedEmails.has(u.email));
        if (allSelected) {
            setOnboardingPreviewSelectedEmails(new Set());
        } else {
            setOnboardingPreviewSelectedEmails(
                new Set(users.map((u) => u.email).filter(Boolean))
            );
        }
    };

    const onboardingPreviewUsersSorted = useMemo(() => {
        const users = onboardingPreviewData?.users ?? [];
        const { field, dir } = onboardingPreviewSort;
        const mul = dir === 'asc' ? 1 : -1;
        return [...users].sort(
            (a, b) => mul * compareOnboardingPreviewUsers(a, b, field)
        );
    }, [onboardingPreviewData?.users, onboardingPreviewSort]);

    const handleOnboardingPreviewSort = (field) => {
        setOnboardingPreviewSort((prev) => ({
            field,
            dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc',
        }));
    };

    const saveOnboardingConfigModal = async () => {
        if (!onboardingModalDraft) return;
        const companyId = store.currentCompany.company_id;
        try {
            await companyStore.saveOnboardingSettings(companyId, {
                enabled: true,
                templateId: onboardingModalDraft.templateId ? Number(onboardingModalDraft.templateId) : undefined,
                nameTemplate: onboardingModalDraft.nameTemplate?.trim() || 'Employee Onboarding {{MMMM}} {{YYYY}}',
                sendingTime: onboardingModalDraft.sendingTime || '08:00',
                remindersEnabled: onboardingModalDraft.remindersEnabled,
                remindersSettings: {
                    channel: onboardingModalDraft.remindersChannel,
                    steps: onboardingModalDraft.remindersSteps,
                },
                lastSuccessfulRun: onboardingModalDraft.lastSuccessfulRun
                    ? moment(onboardingModalDraft.lastSuccessfulRun).toISOString()
                    : undefined,
                createAsDraft: onboardingModalDraft.createAsDraft !== false,
            });
            closeOnboardingConfigModal();
        } catch (e) {
            commonStore.error(companyStore.error || 'Failed to save');
        }
    };

    const createOptionsArray = (languages, enabledOnly) => {
        let optionsArray = [];
        if(enabledOnly) {
            languages = languages.filter((lang) => enabledLanguages?.includes(lang.code)) || [];
        }
        for (let i = 0; i < languages.length; i++) {
            optionsArray.push({
                key: languages[i].code,
                value: languages[i].code,
                name: languages[i].english_name,
                img: languages[i].flag_image,
            });
        }
        return optionsArray;
    };

    const onChangeCompany = async (id) => {
        await store.setCurrentCompany(id, true);
    };

    const hasAnonymousLogging = currentCompany.anonymousTracking;

    return (
        <Block>
            <SpaceBetweenDiv>
                <FlexDiv column>
                    <FlexDiv alignCenter>
                        <Heading2 mb={4}>
                            {currentCompany && currentCompany.company_name}
                        </Heading2>
                        {serverData.length > 1 && (
                            <FlexDiv ml={4} mr={8} link>
                                <Dropdown
                                    isOpen={dropdownOpen}
                                    toggle={toggleCompanyDropdown}
                                >
                                    <DropdownToggle caret tag="span" />
                                    <DropdownMenu>
                                        {serverData.map((company) => {
                                            if (
                                                company.company_name !==
                                                currentCompany.company_name &&
                                                company.role && company.role === 1
                                            )
                                                return (
                                                    <ThemedDropdownItem
                                                        key={company.company_id}
                                                        onClick={() =>
                                                            onChangeCompany(
                                                                company.company_id
                                                            )
                                                        }
                                                    >
                                                        {company.company_name}
                                                    </ThemedDropdownItem>
                                                );
                                        })}
                                    </DropdownMenu>
                                </Dropdown>
                            </FlexDiv>
                        )}
                    </FlexDiv>
                    <TextDefault lighter>Manage company settings</TextDefault>
                </FlexDiv>
                {currentCompany.logo_name ? (
                    <Image
                        link
                        src={
                            import.meta.env.REACT_APP_RESOURCES_URL +
                            '/' +
                            import.meta.env.REACT_APP_COMPANY_RESOURCES_DIR +
                            '/' +
                            currentCompany.logo_name
                        }
                        height={64}
                        alt="Your logo"
                        onClick={onCustomizeAppearance}
                    />
                ) : (
                    <Image
                        link
                        src="/static/img/logo-image.svg"
                        alt="Add your logo"
                        height={64}
                        onClick={onCustomizeAppearance}
                    />
                )}
            </SpaceBetweenDiv>

            <Setting
                name="Name"
                value={currentCompany.company_name}
                actionName="Change"
                action={openEditModal}
            />
            <Setting
                name="Appearance"
                actionName="Customize"
                action={onCustomizeAppearance}
            />
            {(currentCompanyUISettings['ui.certificates_enabled'] !== '0' ||
                !currentCompany.settings) && (
                <Setting
                    name="Certificates"
                    actionName="Preview"
                    action={downloadCertificate}
                />
            )}

            <SettingSelect
                name="Default language for employees"
                options={createOptionsArray(languages, true)}
                defaultValue={currentCompany.defaultLanguage || 'en'}
                action={onChangeLanguage}
            />
            <SettingMultiSelect
                name="Available languages for employees"
                options={createOptionsArray(languages)}
                value={enabledLanguages}
                action={(value) => (setEnabledLanguages(value))}
            />
            {currentCompanyUISettings['ui.menu_show_assessment-insight_tab'] === '1' && (
                <Setting
                    name="Insights customization"
                    actionName="Change"
                    action={() => setShowBrandingModal(true)}
                    centered
                />
            )}
            <Setting
                name="Privacy Policy"
                actionName="Change"
                action={() => setShowPrivacyModal(true)}
                centered
            />
            <Setting
                name="User logging"
                value={hasAnonymousLogging ? 'Disabled' : 'Enabled'}
                actionName={hasAnonymousLogging ? '' : 'Disable'}
                action={onMakeAnonymous}
                helpText="PLEASE NOTE: Once user logging is disabled, you cannot enable it again."
                helpId={'PopoverUserLogging' + currentCompany.company_id}
            />
            <Setting
                name="Send enrolment email to new employees"
                value={
                    currentCompanyUISettings['ui.sendEnrolment'] === '1'
                        ? 'Enabled'
                        : 'Disabled'
                }
                actionName={
                    currentCompanyUISettings['ui.sendEnrolment'] === '1'
                        ? 'Disable'
                        : 'Enable'
                }
                action={onChangeNewEmployeeEnrolment}
                subtext="When Enabled, if a new employee is assigned to a list with a training, they get sent a welcome email with a sign in link"
            />
            <Setting
                name="Send assessment invite when users are added to a list"
                value={
                    currentCompanyUISettings['ui.assessment.inviteOnListAdd'] ===
                    '1'
                        ? 'Enabled'
                        : 'Disabled'
                }
                actionName={
                    currentCompanyUISettings['ui.assessment.inviteOnListAdd'] ===
                    '1'
                        ? 'Disable'
                        : 'Enable'
                }
                action={onChangeInviteOnListAdd}
                actionLoading={companyStore.savingInviteOnListAddSetting.includes(
                    store.currentCompany.company_id
                )}
                subtext="When Enabled, users added to a list that is already on an active, sent assessment receive the assessment invitation. Off by default."
            />
            <Setting
                name="Hide obsolete bounced emails"
                value={hideObsoleteBouncesEnabled ? 'Enabled' : 'Disabled'}
                actionName={hideObsoleteBouncesEnabled ? 'Disable' : 'Enable'}
                action={onToggleHideObsoleteBounces}
                actionLoading={companyStore.savingHideObsoleteBouncesSetting.includes(
                    store.currentCompany.company_id
                )}
                subtext="When enabled, bounced addresses that are no longer on this training or assessment's lists are omitted from the bounced report. Off by default."
            />
            <Setting
                name="Allow fast-forward in MY videos"
                value={fastForwardDisabled ? 'Disabled' : 'Enabled'}
                actionName={fastForwardDisabled ? 'Enable' : 'Disable'}
                action={onToggleFastForward}
                actionLoading={companyStore.savingFastForwardSetting.includes(
                    store.currentCompany.company_id
                )}
                subtext="When disabled, learners cannot fast-forward videos in the MY portal."
            />
            <Setting
                name="Organize employee lists by organization"
                value={
                    organizeListsByOrganizationEnabled
                        ? 'Enabled'
                        : 'Disabled'
                }
                actionName={
                    organizeListsByOrganizationEnabled ? 'Disable' : 'Enable'
                }
                action={onToggleOrganizeListsByOrganization}
                actionLoading={companyStore.savingOrganizeEmployeeListsByOrganization.includes(
                    store.currentCompany.company_id
                )}
                subtext="When enabled, batch-sync can attach lists to parent organizations (metadata), and the Employees page and training selection show lists grouped by organization."
            />
            <FlexDiv py={20} borderBottom>
                <FlexDiv flexGrow={1} alignCenter>
                    <TextLead bold lighter>
                        Tag column
                    </TextLead>
                </FlexDiv>
                <FlexDiv column flexGrow={2} ml={32}>
                    <TextTiny>
                        If set, the employee lists table shows a column named
                        after this tag, listing people on each list who have it.
                    </TextTiny>
                </FlexDiv>
                <FlexDiv
                    flexGrow={1}
                    alignCenter
                    justifyEnd
                    style={{ position: 'relative' }}
                >
                    {tagColumnSavedMark && (
                        <TextDefault themed bold mr={16}>
                            Saved!
                        </TextDefault>
                    )}
                    {companyStore.savingTagColumnSetting.includes(
                        store.currentCompany.company_id
                    ) ? (
                        <CircularProgress size={20} />
                    ) : (
                        <TextInput
                            placeholder="e.g. Leder"
                            value={tagColumnDraft}
                            onChange={(e) => {
                                setTagColumnSavedMark(false);
                                setTagColumnDraft(e.target.value);
                            }}
                            onBlur={onSaveTagColumn}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.target.blur();
                                }
                            }}
                        />
                    )}
                </FlexDiv>
            </FlexDiv>
            <Setting
                name="Automated monthly onboarding training"
                value={onboardingEnabled ? 'Enabled' : 'Disabled'}
                actionName={onboardingEnabled ? 'Disable' : 'Enable'}
                action={onChangeOnboardingToggle}
                actionLoading={companyStore.savingOnboardingSettings}
                subtext={
                    <>
                        <TextTiny block>
                            When enabled, new employees added since the last run are
                            automatically assigned an onboarding training campaign each month.
                            {onboardingEnabled && onboardingSettings?.createAsDraft !== false && (
                                <>
                                    {' '}
                                    Next campaign is created as a draft — no emails until you start it.
                                </>
                            )}
                            {onboardingEnabled && (onboardingSettings?.periodFrom || onboardingSettings?.lastSuccessfulRun) && (
                                <>
                                    {' '}
                                    Next period from:{' '}
                                    {moment(
                                        onboardingSettings.lastSuccessfulRun ||
                                            onboardingSettings.periodFrom
                                    ).format('MMMM D, YYYY [at] HH:mm')}
                                    .
                                </>
                            )}
                        </TextTiny>
                        {onboardingEnabled && onboardingTemplates.length > 0 && (
                            <FlexDiv style={{ marginTop: 8 }} gap={1}>
                                <ThemedButton
                                    mode="secondary"
                                    size="tiny"
                                    $noTextTransform
                                    onClick={openOnboardingConfigModal}
                                    text="Configure"
                                />
                                <ThemedButton
                                    mode="secondary"
                                    size="tiny"
                                    $noTextTransform
                                    onClick={openOnboardingPreviewModal}
                                    text="Preview"
                                />
                            </FlexDiv>
                        )}
                    </>
                }
                isLast={
                    currentCompanyUISettings[
                        'ui.aware_expose.feature_enabled'
                    ] !== '1'
                }
            />
            {currentCompanyUISettings['ui.aware_expose.feature_enabled'] ===
                '1' && (
                <Setting
                    name="AwareExpose"
                    value={
                        currentCompanyUISettings['aware_expose.enabled'] === '1'
                            ? 'Enabled'
                            : 'Disabled'
                    }
                    actionName="Configure"
                    action={() => setAwareExposeModalOpen(true)}
                    subtext="Personal threat-profile and educational phishing scenarios available to employees in the MY portal and via a public link."
                    isLast={true}
                />
            )}
            <AwareExposeSettingsModal
                open={awareExposeModalOpen}
                onClose={() => setAwareExposeModalOpen(false)}
                companyId={currentCompany && currentCompany.company_id}
            />
            <Heading2 style={{ marginTop: 24, marginBottom: 8 }}>
                Security
            </Heading2>
            <Heading3 style={{ marginTop: 16, marginBottom: 8 }}>
                End-user portal (MY)
            </Heading3>
            <SettingSelect
                name="Login link validity"
                options={SECURITY_DURATION_OPTIONS}
                defaultValue={
                    currentCompanyUISettings['security.my_login_link_validity'] ||
                    '1d'
                }
                action={async (value) => {
                    await companyStore.saveSecuritySettings(
                        store.currentCompany.company_id,
                        { myLoginLinkValidity: value }
                    );
                }}
                isLast={true}
            />
            <SettingSelect
                name="Session duration"
                options={SECURITY_DURATION_OPTIONS}
                defaultValue={
                    currentCompanyUISettings['security.my_session_duration'] ||
                    '30d'
                }
                action={async (value) => {
                    await companyStore.saveSecuritySettings(
                        store.currentCompany.company_id,
                        { mySessionDuration: value }
                    );
                }}
                isLast={true}
            />
            <div
                style={{
                    marginTop: 24,
                    marginBottom: 24,
                    borderBottom: '1px solid #e0e0e0',
                }}
            />
            <Heading3 style={{ marginBottom: 8 }}>
                Admin portal (LMS)
            </Heading3>
            <SettingSelect
                name="Login link validity"
                options={SECURITY_DURATION_OPTIONS}
                defaultValue={
                    currentCompanyUISettings['security.lms_login_link_validity'] ||
                    '1h'
                }
                action={async (value) => {
                    await companyStore.saveSecuritySettings(
                        store.currentCompany.company_id,
                        { lmsLoginLinkValidity: value }
                    );
                }}
                isLast={true}
            />
            <SettingSelect
                name="Session duration"
                options={SECURITY_DURATION_OPTIONS}
                defaultValue={
                    currentCompanyUISettings['security.lms_session_duration'] ||
                    '1w'
                }
                action={async (value) => {
                    await companyStore.saveSecuritySettings(
                        store.currentCompany.company_id,
                        { lmsSessionDuration: value }
                    );
                }}
                isLast={true}
            />
            <Modal
                isOpen={showEditCompanyModal}
                onRequestClose={closeEditModal}
                className={'Modal auto-size-modal'}
                ariaHideApp={false}
            >
                <CompanyEditContainer
                    company={currentCompany}
                    companyId={currentCompany.company_id}
                    fromNewSettings={true}
                    onSaved={closeEditModal}
                    onRequestClose={closeEditModal}
                />
            </Modal>
            <Modal
                isOpen={showBrandingModal}
                onRequestClose={() => setShowBrandingModal(false)}
                className={'Modal auto-size-modal'}
                ariaHideApp={false}
            >
                <CompanyGenericEditor
                    title={'Insights Customization'}
                    fields={brandingFields}
                    dataSource={currentCompanyUISettings}
                    onSaved={() => {
                        setShowBrandingModal(false);
                    }}
                    onRequestClose={() => setShowBrandingModal(false)}
                />
            </Modal>
            <Modal
                isOpen={showPrivacyModal}
                onRequestClose={() => setShowPrivacyModal(false)}
                className={'Modal auto-size-modal'}
                ariaHideApp={false}
            >
                <CompanyGenericEditor
                    title={'Privacy Policy URL'}
                    fields={pricacyFields}
                    dataSource={currentCompanyUISettings}
                    updateBranding={true}
                    onSaved={() => {
                        setShowPrivacyModal(false);
                    }}
                    onRequestClose={() => setShowPrivacyModal(false)}
                />
            </Modal>
            <Dialog
                open={onboardingConfigModalOpen}
                onClose={closeOnboardingConfigModal}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        minWidth: 520,
                        maxWidth: 720,
                        borderRadius: 2,
                    },
                }}
            >
                {onboardingModalDraft && (
                    <>
                        <DialogTitle>
                            <TextLead bold>Configure onboarding training</TextLead>
                        </DialogTitle>
                        <DialogContent sx={{ p: 3 }}>
                            <FlexDiv column gap={3}>
                                <FlexDiv column  style={{ marginBottom: 12 }}>
                                    <TextLead bold lighter>
                                        Onboarding template
                                    </TextLead>
                                    <TextInput
                                        margin={"none"}
                                        heightSmall
                                        select
                                        stretched
                                        value={onboardingModalDraft.templateId}
                                        onChange={(e) =>
                                            setOnboardingModalDraft((prev) => ({
                                                ...prev,
                                                templateId: e.target.value,
                                            }))
                                        }
                                    >
                                        {onboardingTemplates.map((opt) => (
                                            <MenuItem key={opt.key} value={opt.value}>
                                                {opt.name}
                                            </MenuItem>
                                        ))}
                                    </TextInput>
                                </FlexDiv>
                                <FlexDiv column style={{ marginBottom: 16 }}>
                                    <TextLead bold lighter >
                                        Name template
                                    </TextLead>
                                    <TextInput
                                        id={"name"}
                                        margin={"none"}
                                        heightSmall
                                        value={onboardingModalDraft.nameTemplate}
                                        onChange={(e) =>
                                            setOnboardingModalDraft((prev) => ({
                                                ...prev,
                                                nameTemplate: e.target.value,
                                            }))
                                        }
                                        placeholder="Employee Onboarding {{MMMM}} {{YYYY}}"
                                        size="small"
                                        stretched
                                    />
                                    <TextDefault
                                        lighter
                                        style={{ fontSize: 12, opacity: 0.8, marginTop: -10 }}
                                    >
                                        {'Use {{MMMM}}, {{YYYY}}, {{DD}} for date parts (e.g. "Employee Onboarding {{MMMM}} {{YYYY}}")'}
                                    </TextDefault>
                                </FlexDiv>
                                <FlexDiv column style={{ marginBottom: 16 }}>
                                    <TextLead bold lighter >
                                        Sending time
                                    </TextLead>
                                    <input
                                        type="time"
                                        value={onboardingModalDraft.sendingTime}
                                        onChange={(e) =>
                                            setOnboardingModalDraft((prev) => ({
                                                ...prev,
                                                sendingTime: e.target.value,
                                            }))
                                        }
                                        style={{
                                            padding: '8px 12px',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 4,
                                            fontSize: 14,
                                        }}
                                    />
                                    <TextDefault
                                        lighter
                                        style={{ fontSize: 12, marginTop: 4, opacity: 0.8 }}
                                    >
                                        Campaign sends at this time on the same day, or
                                        next day if task runs later
                                    </TextDefault>
                                </FlexDiv>
                                <FlexDiv column style={{ marginBottom: 16 }}>
                                    <TextLead bold lighter>
                                        Next period from
                                    </TextLead>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        <DateTimePicker
                                            ampm={false}
                                            value={
                                                onboardingModalDraft.lastSuccessfulRun
                                                    ? moment(onboardingModalDraft.lastSuccessfulRun)
                                                    : moment().startOf('day')
                                            }
                                            onChange={(date) =>
                                                setOnboardingModalDraft((prev) => ({
                                                    ...prev,
                                                    lastSuccessfulRun: date
                                                        ? moment(date).toISOString()
                                                        : prev.lastSuccessfulRun,
                                                }))
                                            }
                                            slotProps={{
                                                textField: {
                                                    size: 'small',
                                                    fullWidth: true,
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                    <TextDefault
                                        lighter
                                        style={{ fontSize: 12, marginTop: 4, opacity: 0.8 }}
                                    >
                                        Employees first added to a list after this date
                                        are included in the next onboarding run. Shift it
                                        forward after a gap so older employees are not
                                        caught up.
                                    </TextDefault>
                                </FlexDiv>
                                <FlexDiv column style={{ marginBottom: 16 }}>
                                    <CheckboxInput
                                        checked={onboardingModalDraft.createAsDraft !== false}
                                        onChange={(e) => {
                                            const checked =
                                                typeof e === 'boolean'
                                                    ? e
                                                    : e?.target?.checked ??
                                                      !onboardingModalDraft.createAsDraft;
                                            setOnboardingModalDraft((prev) => ({
                                                ...prev,
                                                createAsDraft: checked,
                                            }));
                                        }}
                                        name="createAsDraft"
                                        label={
                                            <TextLead bold>
                                                Create next campaign as a draft
                                            </TextLead>
                                        }
                                        noYPadding
                                    />
                                    <TextDefault
                                        lighter
                                        style={{ fontSize: 12, marginTop: 4, opacity: 0.8 }}
                                    >
                                        List and training are created on schedule, but no emails
                                        are sent until you start the draft from Trainings. On by
                                        default so the next run can be reviewed first.
                                    </TextDefault>
                                </FlexDiv>
                                <FlexDiv column>
                                    <AutomationReminders
                                        mt={0}
                                        reminders_enabled={onboardingModalDraft.remindersEnabled}
                                        reminders_channel={onboardingModalDraft.remindersChannel}
                                        reminders_steps={onboardingModalDraft.remindersSteps}
                                        slackAvailable={store.slackAvailable ?? false}
                                        teamsAvailable={store.teamsAvailable ?? false}
                                        slackConnected={false}
                                        teamsConnected={false}
                                        onChangeRemindersEnabled={(e) => {
                                            const enabled = typeof e === 'boolean' ? e : e?.target?.checked ?? !onboardingModalDraft.remindersEnabled;
                                            setOnboardingModalDraft((prev) => ({
                                                ...prev,
                                                remindersEnabled: enabled,
                                                remindersSteps: enabled ? prev.remindersSteps : [],
                                            }));
                                        }}
                                        onChangeRemindersChannel={(channel) =>
                                            setOnboardingModalDraft((prev) => ({
                                                ...prev,
                                                remindersChannel: channel,
                                            }))
                                        }
                                        onAddReminderStep={() =>
                                            setOnboardingModalDraft((prev) => ({
                                                ...prev,
                                                remindersSteps: [
                                                    ...prev.remindersSteps,
                                                    { delay: 1, delayType: REMINDER_DELAY_TYPE.DAY },
                                                ],
                                            }))
                                        }
                                        onDeleteReminderStep={(step) =>
                                            setOnboardingModalDraft((prev) => ({
                                                ...prev,
                                                remindersSteps: prev.remindersSteps.filter((s) => s !== step),
                                            }))
                                        }
                                        onStepsChange={() =>
                                            setOnboardingModalDraft((prev) => ({ ...prev }))
                                        }
                                        scheduleType={AUTOMATION_SCHEDULE_TYPE.FUTURE}
                                    />
                                </FlexDiv>
                            </FlexDiv>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                            <ThemedButton
                                mode="secondary"
                                text="Cancel"
                                onClick={closeOnboardingConfigModal}
                            />
                            <ThemedButton
                                mode="primary"
                                text="Save"
                                onClick={saveOnboardingConfigModal}
                                disabled={companyStore.savingOnboardingSettings}
                            />
                        </DialogActions>
                    </>
                )}
            </Dialog>
            <Dialog
                open={onboardingPreviewModalOpen}
                onClose={closeOnboardingPreviewModal}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { minWidth: 640, maxWidth: 1040, borderRadius: 2 },
                }}
            >
                <DialogTitle>
                    <TextLead bold>Onboarding preview</TextLead>
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    {onboardingPreviewLoading && (
                        <FlexDiv justifyCenter py={4}>
                            <TextDefault>Loading preview…</TextDefault>
                        </FlexDiv>
                    )}
                    {onboardingPreviewError && (
                        <ErrorMessage error={onboardingPreviewError} />
                    )}
                    {!onboardingPreviewLoading && onboardingPreviewData && (
                        <FlexDiv column gap={3}>
                            <FlexDiv column gap={1}>
                                <TextLead>
                                    On{' '}
                                    {moment(
                                        onboardingPreviewData.nextRunDate
                                    ).format('MMMM D, YYYY [at] h:mm A')}{' '}
                                    training with name "
                                    {onboardingPreviewData.trainingName}" will
                                    be created
                                    {onboardingPreviewData.createAsDraft !== false
                                        ? ' as a draft. No emails will be sent until you start it from Trainings'
                                        : ''}
                                    .
                                </TextLead>
                                <FlexDiv alignCenter gap={1} style={{ marginTop: 4 }}>
                                    {!onboardingReschedulePickerOpen && !onboardingRescheduleDraft && (
                                        <ThemedButton
                                            mode="primary"
                                            size="tiny"
                                            $noTextTransform
                                            onClick={() => {
                                                setOnboardingRescheduleDraft(
                                                    moment(onboardingPreviewData.nextRunDate)
                                                );
                                                setOnboardingReschedulePickerOpen(true);
                                            }}
                                            disabled={onboardingRescheduleLoading}
                                            text="Reschedule"
                                        />
                                    )}
                                    {(onboardingReschedulePickerOpen || onboardingRescheduleDraft) && (
                                        <LocalizationProvider
                                            dateAdapter={AdapterMoment}
                                            localeText={{
                                                cancelButtonLabel: 'Cancel',
                                                okButtonLabel: 'Set',
                                            }}
                                        >
                                            <DateTimePicker
                                                label="Reschedule"
                                                ampm={false}
                                                closeOnSelect={false}
                                                value={
                                                    onboardingRescheduleDraft ??
                                                    moment(onboardingPreviewData.nextRunDate)
                                                }
                                                minDateTime={moment()}
                                                onChange={(date) => {
                                                    setOnboardingRescheduleDraft(
                                                        date ? moment(date) : null
                                                    );
                                                }}
                                                onClose={() => {
                                                    setOnboardingReschedulePickerOpen(false);
                                                }}
                                                slots={{
                                                    actionBar: OnboardingRescheduleActionBar,
                                                }}
                                                slotProps={{
                                                    actionBar: {
                                                        actions: ['cancel', 'accept'],
                                                    },
                                                }}
                                            />
                                        </LocalizationProvider>
                                    )}
                                    {(onboardingReschedulePickerOpen || onboardingRescheduleDraft) && (
                                        <ThemedButton
                                            mode="primary"
                                            size="tiny"
                                            $noTextTransform
                                            onClick={() =>
                                                handleOnboardingReschedule(
                                                    onboardingRescheduleDraft ??
                                                    moment(onboardingPreviewData.nextRunDate)
                                                )
                                            }
                                            disabled={
                                                onboardingRescheduleLoading ||
                                                !onboardingRescheduleDraft
                                            }
                                            text="Save"
                                        />
                                    )}
                                </FlexDiv>
                            </FlexDiv>
                            <FlexDiv column gap={1}>
                                <TextLead>
                                    Next period from:{' '}
                                    {moment(
                                        onboardingPreviewData.periodFrom ||
                                            onboardingPreviewData.lastSuccessfulRun
                                    ).format('MMMM D, YYYY [at] HH:mm')}
                                </TextLead>
                                <TextDefault
                                    lighter
                                    style={{ fontSize: 12, opacity: 0.8 }}
                                >
                                    Users first added to a list after this date
                                    are included below. Shift it to change who
                                    is picked up on the next run.
                                </TextDefault>
                                <FlexDiv alignCenter gap={1} style={{ marginTop: 4 }}>
                                    {!onboardingPeriodFromPickerOpen && !onboardingPeriodFromDraft && (
                                        <ThemedButton
                                            mode="secondary"
                                            size="tiny"
                                            $noTextTransform
                                            onClick={() => {
                                                setOnboardingPeriodFromDraft(
                                                    moment(
                                                        onboardingPreviewData.periodFrom ||
                                                            onboardingPreviewData.lastSuccessfulRun
                                                    )
                                                );
                                                setOnboardingPeriodFromPickerOpen(true);
                                            }}
                                            disabled={onboardingPeriodFromLoading}
                                            text="Change"
                                        />
                                    )}
                                    {(onboardingPeriodFromPickerOpen || onboardingPeriodFromDraft) && (
                                        <LocalizationProvider dateAdapter={AdapterMoment}>
                                            <DateTimePicker
                                                label="Next period from"
                                                ampm={false}
                                                closeOnSelect={false}
                                                value={
                                                    onboardingPeriodFromDraft ??
                                                    moment(
                                                        onboardingPreviewData.periodFrom ||
                                                            onboardingPreviewData.lastSuccessfulRun
                                                    )
                                                }
                                                onChange={(date) => {
                                                    setOnboardingPeriodFromDraft(
                                                        date ? moment(date) : null
                                                    );
                                                }}
                                                slotProps={{
                                                    textField: { size: 'small' },
                                                }}
                                            />
                                        </LocalizationProvider>
                                    )}
                                    {(onboardingPeriodFromPickerOpen || onboardingPeriodFromDraft) && (
                                        <ThemedButton
                                            mode="primary"
                                            size="tiny"
                                            $noTextTransform
                                            onClick={() =>
                                                handleOnboardingPeriodFrom(
                                                    onboardingPeriodFromDraft ??
                                                    moment(
                                                        onboardingPreviewData.periodFrom ||
                                                            onboardingPreviewData.lastSuccessfulRun
                                                    )
                                                )
                                            }
                                            disabled={
                                                onboardingPeriodFromLoading ||
                                                !onboardingPeriodFromDraft
                                            }
                                            text="Save"
                                        />
                                    )}
                                </FlexDiv>
                            </FlexDiv>
                            <FlexDiv column gap={1}>
                                <TextLead bold lighter>
                                    Following users will be included:
                                </TextLead>
                                <FlexDiv gap={1} alignCenter style={{ flexWrap: 'wrap' }}>
                                    <TextField
                                        size="small"
                                        placeholder="Email to add"
                                        value={onboardingAddUserInput}
                                        onChange={(e) =>
                                            setOnboardingAddUserInput(
                                                e.target.value
                                            )
                                        }
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter')
                                                handleOnboardingAddUser();
                                        }}
                                        sx={{ minWidth: 200 }}
                                    />
                                    <ThemedButton
                                        mode="secondary"
                                        size="tiny"
                                        $noTextTransform
                                        onClick={handleOnboardingAddUser}
                                        disabled={
                                            !onboardingAddUserInput?.trim() ||
                                            onboardingContextLoading
                                        }
                                        text="Add User"
                                    />
                                    <ThemedButton
                                        mode="secondary"
                                        size="tiny"
                                        $noTextTransform
                                        onClick={handleOnboardingExcludeSelected}
                                        disabled={
                                            onboardingPreviewSelectedEmails
                                                .size === 0 ||
                                            onboardingContextLoading
                                        }
                                        text="Exclude"
                                    />
                                </FlexDiv>
                                <Table size="small" sx={{ mt: 1 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    indeterminate={
                                                        onboardingPreviewSelectedEmails
                                                            .size > 0 &&
                                                        onboardingPreviewSelectedEmails
                                                            .size <
                                                            (onboardingPreviewData
                                                                ?.users?.length ||
                                                                0)
                                                    }
                                                    checked={
                                                        (onboardingPreviewData
                                                            ?.users?.length >
                                                            0 &&
                                                            onboardingPreviewData.users.every(
                                                                (u) =>
                                                                    onboardingPreviewSelectedEmails.has(
                                                                        u.email
                                                                    )
                                                            )) ||
                                                        false
                                                    }
                                                    onChange={
                                                        toggleOnboardingPreviewSelectAll
                                                    }
                                                />
                                            </TableCell>
                                            {ONBOARDING_PREVIEW_SORT_COLUMNS.map(
                                                ({ field, label }) => (
                                                    <TableCell
                                                        key={field}
                                                        sortDirection={
                                                            onboardingPreviewSort.field ===
                                                            field
                                                                ? onboardingPreviewSort.dir
                                                                : false
                                                        }
                                                    >
                                                        <TableSortLabel
                                                            active={
                                                                onboardingPreviewSort.field ===
                                                                field
                                                            }
                                                            direction={
                                                                onboardingPreviewSort.field ===
                                                                field
                                                                    ? onboardingPreviewSort.dir
                                                                    : 'asc'
                                                            }
                                                            onClick={() =>
                                                                handleOnboardingPreviewSort(
                                                                    field
                                                                )
                                                            }
                                                        >
                                                            <TextTiny bold>
                                                                {label}
                                                            </TextTiny>
                                                        </TableSortLabel>
                                                    </TableCell>
                                                )
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {onboardingPreviewUsersSorted.map(
                                            (user) => (
                                                <TableRow key={user.email}>
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            checked={onboardingPreviewSelectedEmails.has(
                                                                user.email
                                                            )}
                                                            onChange={() =>
                                                                toggleOnboardingPreviewUserSelection(
                                                                    user.email
                                                                )
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextTiny>
                                                            {user.email}
                                                        </TextTiny>
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextTiny>
                                                            {user.name || user.email}
                                                        </TextTiny>
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextTiny>
                                                            {(user.lists || []).length
                                                                ? user.lists.join(', ')
                                                                : '—'}
                                                        </TextTiny>
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextTiny>
                                                            {user.dateAdded
                                                                ? moment(
                                                                      user.dateAdded
                                                                  ).format(
                                                                      'MMM D, YYYY'
                                                                  )
                                                                : '—'}
                                                        </TextTiny>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        )}
                                    </TableBody>
                                </Table>
                                <TextDefault
                                    lighter
                                    style={{
                                        fontSize: 12,
                                        opacity: 0.8,
                                        marginTop: 8,
                                    }}
                                >
                                    {(onboardingPreviewData?.users ?? []).length}{' '}
                                    {(onboardingPreviewData?.users ?? []).length ===
                                    1
                                        ? 'user total'
                                        : 'users total'}
                                </TextDefault>
                                {(onboardingPreviewData?.excludedUsers ?? []).length >
                                    0 && (
                                    <FlexDiv column gap={1} style={{ marginTop: 8 }}>
                                        <TextLead bold lighter>
                                            Excluded users
                                        </TextLead>
                                        <FlexDiv
                                            gap={1}
                                            style={{
                                                flexWrap: 'wrap',
                                                alignItems: 'center',
                                            }}
                                        >
                                            {onboardingPreviewData.excludedUsers.map(
                                                (u) => (
                                                    <Chip
                                                        key={u.email}
                                                        label={
                                                            u.name && u.name !== u.email
                                                                ? `${u.name} (${u.email})`
                                                                : u.email
                                                        }
                                                        onDelete={() =>
                                                            handleOnboardingRemoveExcluded(
                                                                u.email
                                                            )}
                                                        disabled={
                                                            onboardingRemovingExcludedEmail ===
                                                            u.email
                                                        }
                                                        deleteIcon={
                                                            onboardingRemovingExcludedEmail ===
                                                            u.email ? (
                                                                <CircularProgress
                                                                    size={16}
                                                                    sx={{
                                                                        mx: 0.5,
                                                                    }}
                                                                />
                                                            ) : undefined
                                                        }
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                )
                                            )}
                                        </FlexDiv>
                                    </FlexDiv>
                                )}
                            </FlexDiv>
                        </FlexDiv>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <ThemedButton
                        mode="secondary"
                        text="Close"
                        onClick={closeOnboardingPreviewModal}
                    />
                </DialogActions>
            </Dialog>
        </Block>
    );
});
